import api from "app/api";
import { Options } from "common";
import { BookingOptionItem } from "features/booking-options/bookingOptionApi";
import { BillingData } from "features/bookings/BillingDataInput";
import { EventItem, EventOption } from "features/events/eventApi";
import { OptionGroupItem } from "features/option-groups/optionGroupApi";
import { OptionItem } from "features/options/optionApi";
import { PartnerItem } from "features/partners/partnerApi";
import { ProgramVariantItem } from "features/program-variants/programVariantApi";
import {
  BOOKING_PAY_LOCATIONS,
  BOOKING_STATUSES,
  PAYMENT_STATUSES,
} from "./bookingConstants";
import { normalizePrivateBookingErrors } from "./bookingValidationErrors";

export interface BookingReadyHistoryEntry {
  at: string;
  by: string;
  ready: boolean;
}

export interface BookingItem {
  id: number;
  booking_number: string;
  external_booking_number?: string;
  event_id: number;
  event?: EventItem;
  program_variant_id: number;
  program_variant?: ProgramVariantItem;
  custom_partner: boolean;
  partner_name: string;
  partner_id: number;
  partner?: PartnerItem;
  status: BOOKING_STATUSES;
  name: string;
  email: string;
  phone: string;
  number_of_adults: number;
  number_of_kids: number;
  number_of_babies: number;
  number_of_guests: number;
  number_of_guides: number;
  paid: boolean;
  check_in_at: string;
  armband_at: string;
  description: string;
  sku: string;
  adult_price: number;
  kid_price: number;
  price: number;
  planned: boolean;
  planned_event_id: number;
  planned_event: EventItem;
  planned_datetime: string;
  estimated_start_time: string;
  table_numbers: string;
  self_seating_pin?: string | null;
  upgrade_price?: number | null;
  rebook_price?: number | null;
  upgrade?: boolean;
  refund_amount?: number | null;
  upgrade_payable?: boolean;
  upgrade_paid?: boolean;
  rebook_paid?: boolean;
  voucher?: string;
  pay_location?: BOOKING_PAY_LOCATIONS;
  booking_options?: BookingOptionItem[];
  created_at: string;
  deleted_at?: string;
  payment_options?: {
    payment_method: string;
    transaction_id: string;
    discount_total: number;
    curency: string;
    kid_price: number;
    adult_price: number;
    admin_fee: number;
  };
  admin_fee: number;
  lang: string;
  currency: string;
  source_id: number;
  source?: PartnerItem;
  created_by_partner_id?: number;
  created_by_partner?: PartnerItem;
  should_notify?: boolean;
  notify_description?: string;
  booking_ready_at?: string | null; //Datetiem string
  booking_ready_history?: BookingReadyHistoryEntry[];
  food_ready_at?: string | null; //Datetiem string
  food_ready_history?: BookingReadyHistoryEntry[];
  customer_requests_invoice?: boolean;
  billing_data?: BillingData;
  payment_status?: PAYMENT_STATUSES | null;
  web_comment_handled?: boolean;
  refund_eligible?: boolean | null;
  refund_at?: string | null;
  notified_at?: string | null;
}
//Add database attributes
export type ExtendedEventOption = OptionItem & EventOption;

export type BookingOptionGroupItem = {
  items: ExtendedEventOption[];
} & OptionGroupItem;

export type BookingOptions = Options<
  "role" | "status" | "partner_id" | "recent_partner_id" | "booking_options"
>;

export function getBookingService(scope?: any, customScope?: string) {
  return api.getService<BookingItem, BookingItem, BookingOptions>("bookings", {
    scope,
    itemExporter(data) {
      if (data?.email) {
        data.email = data.email.trim();
      }
      if (!!data?.name) {
        data.name = data.name.trim();
      }
      if (!!data?.partner_name) {
        data.partner_name = data.partner_name.trim();
      }
      return data;
    },
    urlParams: customScope ? { scope: customScope } : undefined,
    //params goes here
  });
}
export function bookingSearch(params?: any) {
  return api.get<BookingItem[]>("bookings/search", {
    params,
  });
}
export function setTableNumbers(id: any, tableNumbers: string[] | null) {
  return api.post<BookingItem>(`bookings/${id}/set-table-numbers`, {
    tableNumbers,
  });
}
export function checkIn(id: any) {
  return api.post<BookingItem>(`bookings/${id}/check-in`);
}
export function checkOut(id: any) {
  return api.post<BookingItem>(`bookings/${id}/check-out`);
}
export function registerArmband(id: any) {
  return api.post<BookingItem>(`bookings/${id}/register-armband`);
}
export function cancelArmband(id: any) {
  return api.post<BookingItem>(`bookings/${id}/cancel-armband`);
}

export function resendSelfSeatingInvitation(id: any) {
  return api.post<{ queued: boolean }>(
    `bookings/${id}/resend-self-seating-invitation`,
  );
}

export function cancelBookingsByIds(bookingIds?: any[]) {
  return api.post<{ count: number }>(`booking/cancel`, {
    bookingIds,
  });
}

export function getUpgradeVariants(id: any) {
  return api.get<ProgramVariantItem[]>(`bookings/${id}/upgrade-variants`);
}

export type UpgradeItem = Partial<BookingItem>;
export function upgrade(id: any, item: UpgradeItem) {
  return api.post<ProgramVariantItem[]>(`bookings/${id}/upgrade`, item);
}

export interface SetBookingReadyResponse {
  success: boolean;
  booking_ready: boolean;
  booking_ready_history: BookingReadyHistoryEntry[];
}

export interface SetFoodReadyResponse {
  success: boolean;
  food_ready: boolean;
  food_ready_history: BookingReadyHistoryEntry[];
}

export function setBookingReady(id: any, value: boolean) {
  return api.post<SetBookingReadyResponse>(`bookings/${id}/set-booking-ready`, {
    value,
  });
}

export function setFoodReady(id: any, value: boolean) {
  return api.post<SetFoodReadyResponse>(`bookings/${id}/set-food-ready`, {
    value,
  });
}

export function getWebCommentCount() {
  return api.get<{ count: number }>("bookings/web-comment-count");
}

export function getRefundEligibleCount() {
  return api.get<{ count: number }>("bookings/refund-eligible-count");
}

export function setRefundEligible(id: any, value: boolean = true) {
  return api.post<BookingItem>(
    `bookings/${id}/set-refund-eligible`,
    value === false ? { value } : undefined,
  );
}

export function setRefundAt(id: any, value: boolean | false = true) {
  return api.post<BookingItem>(
    `bookings/${id}/set-refund-at`,
    value === false ? { value } : undefined,
  );
}

export function setWebCommentHandled(id: any, value: boolean = true) {
  return api.post<BookingItem>(`bookings/${id}/set-web-comment-handled`, {
    value,
  });
}

export type PaymentUrlResponse = {
  transaction_id?: string | number;
  token: string;
  expires_at: string;
  amount: number;
  currency: string;
  url: string;
};

export function generatePaymentUrl(params: {
  booking_id: number;
  expires_in_minutes?: number;
  lang?: "hu" | "en";
}) {
  return api.post<PaymentUrlResponse>("payments/generate-url", params);
}

export function regeneratePaymentUrl(params: {
  booking_id: number;
  expires_in_minutes?: number;
  lang?: "hu" | "en";
}) {
  return api.post<PaymentUrlResponse>("payments/regenerate-url", params);
}

export type PaymentTransactionInfo = Record<string, any> | null;

export type PaymentStatusResponse = {
  booking_id: number;
  booking_paid: boolean;
  active_transaction: PaymentTransactionInfo;
  latest_transaction: PaymentTransactionInfo;
};

export interface PrivateEventData {
  boat_id: number;
  start_time: string;
  duration: number;
  number_of_places?: number;
  number_of_online_places?: number;
  places_tolerance?: number;
  program_id: number;
  limits?: any[];
  variants?: any[];
  options?: any[];
  seating_layouts?: any[];
}

export async function createPrivateEvent(
  eventData: PrivateEventData,
  booking: Partial<BookingItem>,
) {
  const response = await api.post<BookingItem>("bookings/create-private-event", {
    event_group: {
      boat_id: eventData.boat_id,
      start_time: eventData.start_time,
      duration: eventData.duration,
      number_of_places: eventData.number_of_places,
      number_of_online_places: eventData.number_of_online_places,
      places_tolerance: eventData.places_tolerance,
    },
    event: {
      program_id: eventData.program_id,
      private: true,
      limits: eventData.limits,
      variants: eventData.variants,
      options: eventData.options,
      seating_layouts: eventData.seating_layouts,
    },
    booking,
  });
  if (
    !response.success &&
    response.error &&
    typeof response.error === "object" &&
    !Array.isArray(response.error)
  ) {
    return {
      ...response,
      error: normalizePrivateBookingErrors(response.error),
    };
  }
  return response;
}

export function getPaymentStatus(params: { booking_id: number }) {
  return api.get<PaymentStatusResponse>("payments/status", {
    params,
  });
}

export function sendPaymentUrl(params: {
  booking_id: number;
  email?: string;
  lang?: string;
}) {
  return api.post<{ success?: boolean }>("payments/send-url", params);
}
